import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";

const API_URL = process.env.REACT_APP_API_URL;

const INK = "#1e2a3a";
const MUTED = "#6b7a8d";
const LINE = "#e6ecf3";
const INDIGO = "#5b5bd6";
const GREEN = "#12a150";
const AMBER = "#e5a13a";
const RED = "#e5484d";
const BG = "#f7f9fc";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const pctOf = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0);

const card = {
  background: "#fff",
  border: `1px solid ${LINE}`,
  borderRadius: "14px",
  padding: "16px",
};
const label = { fontSize: "11px", fontWeight: 700, letterSpacing: "0.6px", color: MUTED, textTransform: "uppercase" };

const statusOf = (s) => {
  const t = (s || "").toLowerCase();
  if (t.includes("complete")) return "Completed";
  if (t.includes("progress") || t.includes("ongoing")) return "Ongoing";
  return "Not Started";
};

const Bar = ({ value, color, height = 6 }) => (
  <div style={{ background: "#eef2f7", borderRadius: 99, height, width: "100%" }}>
    <div style={{ width: `${Math.min(value, 100)}%`, background: color, height, borderRadius: 99 }} />
  </div>
);

/* ---------- charts (inline SVG, fixed heights so nothing overflows) ---------- */
const Donut = ({ counts, total }) => {
  const items = [
    ["Completed", counts.Completed || 0, GREEN],
    ["Ongoing", counts.Ongoing || 0, AMBER],
    ["Not Started", counts["Not Started"] || 0, RED],
  ];
  const sum = total || items.reduce((a, [, v]) => a + v, 0) || 1;
  const R = 62, C = 2 * Math.PI * R;
  let off = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap" }}>
      <svg viewBox="0 0 170 170" style={{ width: 160, height: 160, flexShrink: 0 }}>
        <g transform="translate(85,85) rotate(-90)">
          {items.map(([k, v, c]) => {
            const len = (v / sum) * C;
            const el = <circle key={k} r={R} fill="none" stroke={c} strokeWidth="20"
              strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-off} strokeLinecap="butt" />;
            off += len;
            return el;
          })}
        </g>
        <text x="85" y="80" textAnchor="middle" fontSize="26" fontWeight="700" fill={INK}>{sum}</text>
        <text x="85" y="98" textAnchor="middle" fontSize="9" fill={MUTED} letterSpacing="1">TOTAL ACS</text>
      </svg>
      <div style={{ flex: 1, minWidth: 170 }}>
        {items.map(([k, v, c]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${LINE}` }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: INK }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: c, display: "inline-block" }} />{k}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: INK }}>
              {v} <span style={{ color: MUTED, fontWeight: 500 }}>({pctOf(v, sum)}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TrendChart = ({ data }) => {
  const [hover, setHover] = useState(null);
  if (!data || data.length < 2) return <p style={{ color: MUTED, fontSize: 13 }}>Not enough data points yet.</p>;
  const W = 760, H = 260, L = 58, R = 18, T = 18, B = 40;
  const max = Math.max(...data.map((d) => d.value)) * 1.12 || 1;
  const step = (W - L - R) / (data.length - 1);
  const x = (i) => L + i * step;
  const y = (v) => H - B - (v / max) * (H - T - B);
  const line = data.map((d, i) => `${i ? "L" : "M"}${x(i)},${y(d.value)}`).join(" ");
  const area = `${line} L${x(data.length - 1)},${H - B} L${L},${H - B} Z`;
  const ticks = 5;
  // show every label when there's room, otherwise thin them out
  const every = data.length <= 12 ? 1 : Math.ceil(data.length / 12);

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 260 }}
        onMouseLeave={() => setHover(null)}>
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const v = (max / ticks) * i;
          return (
            <g key={i}>
              <line x1={L} y1={y(v)} x2={W - R} y2={y(v)} stroke={LINE} />
              <text x={L - 10} y={y(v) + 4} textAnchor="end" fontSize="10.5" fill={MUTED}>{fmt(Math.round(v))}</text>
            </g>
          );
        })}
        <path d={area} fill="rgba(91,91,214,0.10)" />
        <path d={line} fill="none" stroke={INDIGO} strokeWidth="2.4" />
        {data.map((d, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(d.value)} r={hover === i ? 5 : 3.2} fill={INDIGO} />
            {/* generous invisible hit area for hovering */}
            <rect x={x(i) - step / 2} y={T} width={step} height={H - T - B}
              fill="transparent" onMouseEnter={() => setHover(i)} />
          </g>
        ))}
        {data.map((d, i) =>
          i % every === 0 || i === data.length - 1 ? (
            <text key={i} x={x(i)} y={H - 14} textAnchor="middle" fontSize="10.5" fill={MUTED}>
              {d.label}
            </text>
          ) : null
        )}
      </svg>
      {hover != null && (
        <div style={{
          position: "absolute",
          left: `${(x(hover) / W) * 100}%`,
          top: `${(y(data[hover].value) / H) * 100}%`,
          transform: "translate(-50%, -130%)",
          background: "#1f2430", color: "#fff", padding: "7px 11px",
          borderRadius: 8, fontSize: 12, whiteSpace: "nowrap", pointerEvents: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        }}>
          {data[hover].label}{data[hover].iso ? ` (${data[hover].iso})` : ""}: <strong>{fmt(data[hover].value)}</strong> digitalised booths
        </div>
      )}
    </div>
  );
};

/* ------------------------------- page ------------------------------- */
const CasteCensusDashboard = () => {
  const [raw, setRaw] = useState({ records: [], trend: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("overview");

  const [zone, setZone] = useState("");
  const [district, setDistrict] = useState("");
  const [acName, setAcName] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const authHeader = async () => {
    const token = await localforage.getItem("token");
    return { Authorization: "Bearer " + token };
  };

  const load = async (refresh) => {
    setLoading(true);
    setErr("");
    try {
      const headers = await authHeader();
      const res = await api.get(`${API_URL}/dashboards/caste-census`, {
        headers, params: refresh ? { refresh: "1" } : {},
      });
      setRaw(res.data);
    } catch (e) {
      setErr(e.response?.data?.error || "Could not load the dashboard.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const all = raw.records || [];
  const zones = useMemo(() => [...new Set(all.map((r) => r.zone).filter(Boolean))], [all]);
  const districts = useMemo(
    () => [...new Set(all.filter((r) => !zone || r.zone === zone).map((r) => r.district).filter(Boolean))].sort(),
    [all, zone]
  );
  const acNames = useMemo(
    () => [...new Set(all
      .filter((r) => (!zone || r.zone === zone) && (!district || r.district === district))
      .map((r) => r.acName).filter(Boolean))].sort(),
    [all, zone, district]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((r) =>
      (!zone || r.zone === zone) &&
      (!district || r.district === district) &&
      (!acName || r.acName === acName) &&
      (!status || statusOf(r.status) === status) &&
      (!q || [r.acName, r.district, r.halkaIncharge, r.dpoc, r.mappedResource]
        .some((v) => String(v || "").toLowerCase().includes(q)))
    );
  }, [all, zone, district, acName, status, search]);

  const k = useMemo(() => {
    const totalBooth = rows.reduce((a, r) => a + r.totalBooth, 0);
    const dist = rows.reduce((a, r) => a + r.distributed, 0);
    const digi = rows.reduce((a, r) => a + r.digitalised, 0);
    const counts = { Completed: 0, Ongoing: 0, "Not Started": 0 };
    rows.forEach((r) => { counts[statusOf(r.status)] += 1; });
    return {
      acs: rows.length, totalBooth, dist, digi,
      distPct: pctOf(dist, totalBooth), digiPct: pctOf(digi, totalBooth),
      counts, districts: new Set(rows.map((r) => r.district)).size,
    };
  }, [rows]);

  const zoneSummary = useMemo(() => {
    const by = {};
    rows.forEach((r) => {
      const z = r.zone || "Unknown";
      if (!by[z]) by[z] = { zone: z, acs: 0, total: 0, dist: 0, digi: 0, Completed: 0, Ongoing: 0, "Not Started": 0 };
      by[z].acs += 1; by[z].total += r.totalBooth; by[z].dist += r.distributed; by[z].digi += r.digitalised;
      by[z][statusOf(r.status)] += 1;
    });
    return Object.values(by);
  }, [rows]);

  const districtRows = useMemo(() => {
    const by = {};
    rows.forEach((r) => {
      const d = r.district || "Unknown";
      if (!by[d]) by[d] = { district: d, zone: r.zone, acs: 0, total: 0, dist: 0, digi: 0, completed: 0 };
      by[d].acs += 1; by[d].total += r.totalBooth; by[d].dist += r.distributed; by[d].digi += r.digitalised;
      if (statusOf(r.status) === "Completed") by[d].completed += 1;
    });
    return Object.values(by).sort((a, b) => b.total - a.total);
  }, [rows]);

  const resetFilters = () => { setZone(""); setDistrict(""); setAcName(""); setStatus(""); setSearch(""); };

  const sel = { padding: "9px 11px", border: `1px solid ${LINE}`, borderRadius: "10px", fontSize: "13px", background: "#fff", color: INK, minWidth: 0, width: "100%" };

  const TABS = [
    ["overview", "Executive Overview"],
    ["tracker", `AC Master Tracker (${rows.length})`],
    ["analytics", "Zone & District Analytics"],
    ["field", "Field Intelligence & Remarks"],
  ];

  return (
    <div>
      <Dashboard />
      <div style={{ background: BG, minHeight: "100vh", padding: "18px" }}>
        <div style={{ maxWidth: 1440, margin: "0 auto" }}>

          {/* header */}
          <div style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: INK }}>Caste Census Operations Dashboard</div>
              <div style={{ fontSize: 13, color: MUTED }}>State Monitoring • Assembly Constituency Progress Tracker</div>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {[["Total ACs", k.acs], ["Total Booths", fmt(k.totalBooth)], ["Distributed %", `${k.distPct}%`]].map(([t, v]) => (
                <div key={t} style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: "8px 14px", textAlign: "center", background: "#fff" }}>
                  <div style={{ ...label, fontSize: 10 }}>{t}</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: INK }}>{v}</div>
                </div>
              ))}
              <button type="button" onClick={() => load(true)}
                style={{ background: INDIGO, color: "#fff", border: "none", borderRadius: 10, padding: "10px 16px", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                Refresh
              </button>
            </div>
          </div>

          {/* tabs */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
            {TABS.map(([id, name]) => (
              <button key={id} type="button" onClick={() => setTab(id)}
                style={{
                  background: tab === id ? "#fff" : "transparent",
                  color: tab === id ? INDIGO : MUTED,
                  border: `1px solid ${tab === id ? LINE : "transparent"}`,
                  borderRadius: 10, padding: "9px 16px", fontWeight: 600, cursor: "pointer", fontSize: 13,
                }}>
                {name}
              </button>
            ))}
          </div>

          {/* filters */}
          <div style={{ ...card, marginBottom: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
              <input placeholder="Search AC, district, Halka Incharge, DPOC…" value={search}
                onChange={(e) => setSearch(e.target.value)} style={{ ...sel, gridColumn: "span 2" }} />
              <select value={zone} onChange={(e) => { setZone(e.target.value); setDistrict(""); setAcName(""); }} style={sel}>
                <option value="">All Zones</option>
                {zones.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
              <select value={district} onChange={(e) => { setDistrict(e.target.value); setAcName(""); }} style={sel}>
                <option value="">All Districts</option>
                {districts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={acName} onChange={(e) => setAcName(e.target.value)} style={sel}>
                <option value="">All ACs</option>
                {acNames.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} style={sel}>
                <option value="">All Status</option>
                <option value="Completed">Completed</option>
                <option value="Ongoing">Ongoing</option>
                <option value="Not Started">Not Started</option>
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 13, color: MUTED }}>
              <span>Showing <strong style={{ color: INK }}>{rows.length}</strong> of {all.length} constituencies</span>
              <button type="button" onClick={resetFilters}
                style={{ background: "none", border: "none", color: INDIGO, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                Reset All Filters
              </button>
            </div>
          </div>

          {err && <p style={{ color: RED, textAlign: "center", fontWeight: 600 }}>{err}</p>}
          {loading && <p style={{ textAlign: "center", color: MUTED }}>Loading dashboard…</p>}

          {!loading && !err && (
            <>
              {tab === "overview" && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginBottom: 14 }}>
                    <div style={card}>
                      <div style={label}>Assembly Coverage</div>
                      <div style={{ fontSize: 30, fontWeight: 800, color: INK, marginTop: 6 }}>{k.acs}<span style={{ fontSize: 14, fontWeight: 600, color: MUTED }}> Constituencies</span></div>
                      <div style={{ fontSize: 12, color: MUTED }}>Across {k.districts} districts</div>
                      <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 10, paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <span style={{ color: MUTED }}>Total Polling Booths</span><strong>{fmt(k.totalBooth)}</strong>
                      </div>
                    </div>
                    <div style={card}>
                      <div style={label}>Voter List Distributed</div>
                      <div style={{ fontSize: 30, fontWeight: 800, color: INK, marginTop: 6 }}>{fmt(k.dist)} <span style={{ fontSize: 14, color: GREEN, fontWeight: 700 }}>({k.distPct}%)</span></div>
                      <div style={{ marginTop: 8 }}><Bar value={k.distPct} color={GREEN} /></div>
                      <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 10, paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <span style={{ color: MUTED }}>Target Booth Coverage</span><strong>{fmt(k.totalBooth)}</strong>
                      </div>
                    </div>
                    <div style={card}>
                      <div style={label}>Data Digitalised</div>
                      <div style={{ fontSize: 30, fontWeight: 800, color: INK, marginTop: 6 }}>{fmt(k.digi)} <span style={{ fontSize: 14, color: INDIGO, fontWeight: 700 }}>({k.digiPct}%)</span></div>
                      <div style={{ marginTop: 8 }}><Bar value={k.digiPct} color={INDIGO} /></div>
                      <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 10, paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <span style={{ color: MUTED }}>Digital Completion Rate</span><strong>{k.digiPct}%</strong>
                      </div>
                    </div>
                    <div style={card}>
                      <div style={label}>Completion Status</div>
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        {[["Done", k.counts.Completed, GREEN, "#eafaf1"], ["Ongoing", k.counts.Ongoing, AMBER, "#fdf5e6"], ["Pending", k.counts["Not Started"], RED, "#fdecec"]].map(([t, v, c, bg]) => (
                          <div key={t} style={{ flex: 1, background: bg, borderRadius: 10, padding: "10px 6px", textAlign: "center" }}>
                            <div style={{ fontSize: 20, fontWeight: 800, color: c }}>{v}</div>
                            <div style={{ fontSize: 11, color: MUTED }}>{t}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: MUTED, textAlign: "center", marginTop: 8 }}>
                        {pctOf(k.counts.Completed, k.acs)}% of ACs fully completed
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 380px) 1fr", gap: 14, marginBottom: 14 }}>
                    <div style={card}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>AC Completion Breakdown</div>
                      <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>Distribution across 3 status stages</div>
                      <Donut counts={k.counts} total={k.acs} />
                    </div>
                    <div style={card}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>Daily Digitalisation Progress Trend</div>
                      <div style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>
                        Aggregated daily booth count (state-wide)
                        {raw.trend && raw.trend.length
                          ? ` — ${raw.trend[0].label} to ${raw.trend[raw.trend.length - 1].label}`
                          : ""}
                      </div>
                      <TrendChart data={raw.trend} />
                    </div>
                  </div>

                  <div style={card}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>Zone Progress Summary</div>
                    <div style={{ fontSize: 12, color: MUTED, marginBottom: 12 }}>Comparative overview across zones</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
                      {zoneSummary.map((z) => (
                        <div key={z.zone} style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 14 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <strong style={{ color: INK, fontSize: 15 }}>{z.zone} Zone</strong>
                            <span style={{ background: "#eef0fb", color: INDIGO, borderRadius: 99, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{z.acs} ACs</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                            <span style={{ color: MUTED }}>Distributed Booths</span>
                            <strong>{fmt(z.dist)} / {fmt(z.total)} ({pctOf(z.dist, z.total)}%)</strong>
                          </div>
                          <Bar value={pctOf(z.dist, z.total)} color={GREEN} />
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, margin: "10px 0 4px" }}>
                            <span style={{ color: MUTED }}>Digitalised Data</span>
                            <strong>{fmt(z.digi)} / {fmt(z.total)} ({pctOf(z.digi, z.total)}%)</strong>
                          </div>
                          <Bar value={pctOf(z.digi, z.total)} color={INDIGO} />
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 12, borderTop: `1px solid ${LINE}`, paddingTop: 8 }}>
                            <span style={{ color: GREEN }}>{z.Completed} Completed</span>
                            <span style={{ color: AMBER }}>{z.Ongoing} Ongoing</span>
                            <span style={{ color: RED }}>{z["Not Started"]} Pending</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {tab === "tracker" && (
                <div style={{ ...card, padding: 0 }}>
                  <div style={{ padding: "14px 16px", borderBottom: `1px solid ${LINE}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>Displaying {rows.length} Constituency Records</span>
                      <span style={{ fontSize: 12, color: MUTED }}> (live from the Google Sheet)</span>
                    </div>
                  </div>
                  <div style={{ overflowX: "auto", maxHeight: 620, overflowY: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ background: "#f2f5fa" }}>
                          {["AC No", "Constituency (AC)", "District / Zone", "Halka Incharge", "Total Booths", "Distributed", "Digitalised", "Status"].map((h) => (
                            <th key={h} style={{ padding: "11px 12px", textAlign: h === "Constituency (AC)" || h === "District / Zone" || h === "Halka Incharge" ? "left" : "center", borderBottom: `1px solid ${LINE}`, color: MUTED, fontSize: 11, letterSpacing: "0.5px", textTransform: "uppercase", position: "sticky", top: 0, background: "#f2f5fa", whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => {
                          const st = statusOf(r.status);
                          const c = st === "Completed" ? GREEN : st === "Ongoing" ? AMBER : RED;
                          return (
                            <tr key={`${r.acNo}-${i}`} style={{ borderBottom: `1px solid ${LINE}` }}>
                              <td style={{ padding: "11px 12px", textAlign: "center", fontWeight: 700, color: INK }}>#{r.acNo}</td>
                              <td style={{ padding: "11px 12px", fontWeight: 700, color: INK }}>{r.acName}</td>
                              <td style={{ padding: "11px 12px" }}>
                                <div style={{ color: INK }}>{r.district}</div>
                                <div style={{ color: MUTED, fontSize: 11 }}>{r.zone} Zone</div>
                              </td>
                              <td style={{ padding: "11px 12px" }}>{r.halkaIncharge || "Not Appointed"}</td>
                              <td style={{ padding: "11px 12px", textAlign: "center" }}>{fmt(r.totalBooth)}</td>
                              <td style={{ padding: "11px 12px", textAlign: "center", minWidth: 110 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: INK, marginBottom: 3 }}>{fmt(r.distributed)}</div>
                                <Bar value={r.distPct} color={GREEN} height={5} />
                              </td>
                              <td style={{ padding: "11px 12px", textAlign: "center", fontWeight: 700, color: INDIGO }}>{fmt(r.digitalised)}</td>
                              <td style={{ padding: "11px 12px", textAlign: "center" }}>
                                <span style={{ background: `${c}18`, color: c, borderRadius: 8, padding: "4px 12px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{st}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {tab === "analytics" && (
                <div style={card}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: INK }}>District Performance Leaderboard</div>
                  <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 14 }}>
                    Aggregated voter list distribution and digitalisation across districts
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
                    {districtRows.map((d) => (
                      <div key={d.district} style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 16 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>{d.district}</div>
                            <div style={{ fontSize: 11.5, color: MUTED }}>{d.zone} Zone</div>
                          </div>
                          <span style={{ border: `1px solid ${LINE}`, borderRadius: 8, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, color: INK, whiteSpace: "nowrap" }}>
                            {d.completed} / {d.acs} ACs Completed
                          </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 5 }}>
                          <span style={{ color: MUTED }}>Distributed Coverage:</span>
                          <strong style={{ color: GREEN }}>{pctOf(d.dist, d.total)}%</strong>
                        </div>
                        <Bar value={pctOf(d.dist, d.total)} color={GREEN} height={7} />
                        <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 12, paddingTop: 10 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 6 }}>
                            <span style={{ color: MUTED }}>Total Polling Booths:</span>
                            <strong style={{ color: INK }}>{fmt(d.total)}</strong>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
                            <span style={{ color: MUTED }}>Digitalised Booths:</span>
                            <strong style={{ color: INDIGO }}>{fmt(d.digi)}</strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tab === "field" && (
                <div style={card}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: INK }}>Ground Field Intelligence &amp; Remarks</div>
                  <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 14 }}>
                    Direct field notes and qualitative feedback recorded for constituencies
                  </div>
                  {rows.filter((r) => r.remark).length === 0 ? (
                    <p style={{ color: MUTED, fontSize: 13 }}>No remarks recorded for the current filter.</p>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 14 }}>
                      {rows.filter((r) => r.remark).map((r, i) => {
                        const st = statusOf(r.status);
                        const c = st === "Completed" ? GREEN : st === "Ongoing" ? AMBER : RED;
                        return (
                          <div key={`${r.acNo}-${i}`} style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 16 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 12 }}>
                              <div style={{ fontSize: 14.5, fontWeight: 700, color: INK }}>
                                {r.acName} <span style={{ color: MUTED, fontWeight: 500, fontSize: 12.5 }}>({r.district} • #{r.acNo})</span>
                              </div>
                              <span style={{ background: `${c}18`, color: c, borderRadius: 8, padding: "4px 11px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{st}</span>
                            </div>
                            <div style={{ border: `1px solid ${LINE}`, background: "#fbfcfe", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#374151", fontStyle: "italic", lineHeight: 1.55 }}>
                              &ldquo;{r.remark}&rdquo;
                            </div>
                            <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 12, paddingTop: 10, display: "flex", justifyContent: "space-between", fontSize: 12.5, flexWrap: "wrap", gap: 6 }}>
                              <span style={{ color: MUTED }}>Incharge: <strong style={{ color: INK }}>{r.halkaIncharge || "Not Appointed"}</strong></span>
                              <span style={{ color: MUTED }}>Digitalised: <strong style={{ color: INDIGO }}>{r.pct}%</strong></span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CasteCensusDashboard;
